import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { ChevronLeft, ChevronRight, RefreshCw, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getContactBookingHistory } from '../../api/contacts'
import { DEFAULT_BOOKING_STATUSES, bookingStatusStyle, fromServerStatus } from '../../lib/bookings/bookingStatus'
import { formatMoney } from '../../lib/money'
import { useTenantCurrencyState } from '../../hooks/useTenantCurrency'

const PAGE_LIMIT = 20

// The status keys and colours are the bookings page's: one vocabulary.
const STATUS_OPTIONS = ['all', ...DEFAULT_BOOKING_STATUSES]

const STATUS_FALLBACKS = {
  pending: 'Pending',
  awaiting_deposit: 'Awaiting deposit',
  awaiting_confirmation: 'Awaiting confirmation',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

function emptyHistory(offset = 0) {
  return { total: 0, limit: PAGE_LIMIT, offset, items: [] }
}

function numberOrZero(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function getStatusLabel(t, status) {
  const key = fromServerStatus(status)
  return t(`contacts.bookingStatus.${key}`, {
    defaultValue: STATUS_FALLBACKS[key] || key,
  })
}

function getHistoryItems(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.bookings)) return payload.bookings
  return []
}

function getBookingCode(booking) {
  return booking.booking_code || booking.bookingCode || booking.code || booking.id || '—'
}

function normalizeTimeSlot(value) {
  if (!value || typeof value !== 'string') return ''
  return value.slice(0, 5)
}

function formatDateOnly(value) {
  if (!value) return '—'
  const raw = String(value)
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (match) return `${match[3]}/${match[2]}/${match[1]}`
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  return date.toLocaleDateString('vi-VN')
}

function formatDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatBookingDateTime(booking) {
  const date = formatDateOnly(booking.date || booking.booking_date)
  const time = normalizeTimeSlot(booking.time_slot || booking.time)
  return time ? `${date} ${time}` : date
}

function getErrorMessage(err, t) {
  const status = err?.response?.status
  if (status === 401) return t('common.unauthorized')
  if (status === 403) return t('contacts.bookingPermissionDenied')
  if (status === 404) return t('contacts.bookingContactNotFound')
  if (status === 422) {
    const detail = err?.response?.data?.detail
    return typeof detail === 'string' ? detail : t('contacts.bookingValidationError')
  }
  return t('contacts.bookingHistoryError')
}

function SummaryTile({ label, value, loading }) {
  return (
    <div className="bg-white rounded-2xl px-3 py-3" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-lg font-bold text-gray-900 mt-0.5">{loading ? '...' : value}</div>
    </div>
  )
}

function BookingStatusPill({ status }) {
  const { t } = useTranslation()
  const key = fromServerStatus(status)
  const style = bookingStatusStyle(key)
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${style.bg} ${style.text}`}>
      {getStatusLabel(t, key)}
    </span>
  )
}

function BookingInfoRow({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-sm font-semibold text-gray-800 mt-0.5 break-words">{value || '—'}</div>
    </div>
  )
}

export default function ContactBookingHistory({
  tenantId,
  contactId,
  bookingSummary,
  summaryLoading = false,
  canViewHistory = true,
}) {
  const { t, i18n } = useTranslation()
  // Bookings carry no currency of their own: deposits use the tenant currency,
  // shown only once resolved (unresolved means the VND placeholder).
  const { currency, isResolved: currencyResolved } = useTenantCurrencyState()
  const requestSeq = useRef(0)
  const summary = bookingSummary && typeof bookingSummary === 'object' ? bookingSummary : {}
  const [status, setStatus] = useState('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [offset, setOffset] = useState(0)
  const [history, setHistory] = useState(() => emptyHistory())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const hasDateRangeError = Boolean(fromDate && toDate && fromDate > toDate)
  const hasActiveFilters = status !== 'all' || Boolean(fromDate || toDate)
  const params = useMemo(() => {
    const next = {
      limit: PAGE_LIMIT,
      offset,
      sort: hasActiveFilters ? 'booking_date_desc' : 'latest',
    }
    if (status !== 'all') next.status = status
    if (fromDate) next.from_date = fromDate
    if (toDate) next.to_date = toDate
    return next
  }, [fromDate, hasActiveFilters, offset, status, toDate])

  const loadHistory = useCallback(async () => {
    if (!canViewHistory || !tenantId || !contactId || hasDateRangeError) {
      setLoading(false)
      return
    }

    const requestId = requestSeq.current + 1
    requestSeq.current = requestId
    setLoading(true)
    setError('')
    try {
      const data = await getContactBookingHistory(tenantId, contactId, params)
      if (requestSeq.current !== requestId) return
      const items = getHistoryItems(data)
      setHistory({
        total: numberOrZero(data?.total ?? items.length),
        limit: numberOrZero(data?.limit ?? params.limit) || PAGE_LIMIT,
        offset: numberOrZero(data?.offset ?? params.offset),
        items,
      })
    } catch (err) {
      if (requestSeq.current !== requestId) return
      Sentry.captureException(err, {
        tags: { action: 'get_contact_booking_history' },
        extra: { tenantId, contactId, params },
      })
      setHistory(emptyHistory(params.offset))
      setError(getErrorMessage(err, t))
    } finally {
      if (requestSeq.current === requestId) setLoading(false)
    }
  }, [canViewHistory, contactId, hasDateRangeError, params, t, tenantId])

  useEffect(() => {
    setHistory(emptyHistory())
    setError('')
    setOffset(0)
  }, [contactId])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  function clearFilters() {
    setOffset(0)
    setStatus('all')
    setFromDate('')
    setToDate('')
  }

  const items = history.items || []
  const total = numberOrZero(history.total)
  const limit = numberOrZero(history.limit) || PAGE_LIMIT
  const currentOffset = numberOrZero(history.offset)
  const currentPage = Math.floor(currentOffset / limit) + 1
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const hasPrevious = currentOffset > 0
  const hasNext = currentOffset + items.length < total

  return (
    <div className="mt-4">
      <div className="text-base font-bold text-gray-900 mb-3">
        {t('contacts.bookingHistory')}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <SummaryTile label={t('contacts.bookingTotal')} value={numberOrZero(summary.total)} loading={summaryLoading} />
        <SummaryTile label={t('contacts.bookingActive')} value={numberOrZero(summary.active)} loading={summaryLoading} />
        <SummaryTile label={t('contacts.bookingCompleted')} value={numberOrZero(summary.completed)} loading={summaryLoading} />
        <SummaryTile label={t('contacts.bookingCancelled')} value={numberOrZero(summary.cancelled)} loading={summaryLoading} />
      </div>

      {summary.latest_booking_at ? (
        <div className="bg-white rounded-2xl px-4 py-3 mb-3 flex flex-wrap items-center gap-2 text-sm" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <span className="text-gray-400">{t('contacts.latestBooking')}</span>
          <span className="font-semibold text-gray-800">{formatDateTime(summary.latest_booking_at)}</span>
          {summary.latest_booking_status ? <BookingStatusPill status={summary.latest_booking_status} /> : null}
        </div>
      ) : null}

      {!canViewHistory ? (
        <div className="bg-white rounded-2xl p-4 text-sm text-gray-500" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {t('contacts.bookingPermissionDenied')}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl p-4 mb-3" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="flex flex-col gap-1 text-xs text-gray-500">
                {t('contacts.bookingStatusLabel')}
                <select
                  className="border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={status}
                  onChange={e => { setOffset(0); setStatus(e.target.value) }}
                >
                  {STATUS_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option === 'all' ? t('contacts.bookingStatusAll') : getStatusLabel(t, option)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-500">
                {t('contacts.bookingFromDate')}
                <input
                  type="date"
                  className="border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={fromDate}
                  onChange={e => { setOffset(0); setFromDate(e.target.value) }}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-500">
                {t('contacts.bookingToDate')}
                <input
                  type="date"
                  className="border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={toDate}
                  onChange={e => { setOffset(0); setToDate(e.target.value) }}
                />
              </label>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
              <div className="text-xs text-gray-400">
                {t('contacts.bookingShowing', { count: items.length, total })}
              </div>
              <div className="flex items-center gap-2">
                {hasActiveFilters ? (
                  <button
                    type="button"
                    className="h-9 px-3 border border-gray-200 text-gray-600 rounded-md text-sm font-semibold flex items-center gap-1.5 hover:bg-gray-50"
                    onClick={clearFilters}
                  >
                    <X size={14} />
                    {t('contacts.clearBookingFilters')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="h-9 w-9 border border-gray-200 text-gray-600 rounded-md flex items-center justify-center hover:bg-gray-50 disabled:opacity-50"
                  onClick={loadHistory}
                  disabled={loading || hasDateRangeError}
                  title={t('contacts.refreshBookingHistory')}
                  aria-label={t('contacts.refreshBookingHistory')}
                >
                  <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {hasDateRangeError ? (
              <div className="mt-3 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
                {t('contacts.bookingDateRangeInvalid')}
              </div>
            ) : null}
          </div>

          {loading ? (
            <div className="bg-white rounded-2xl p-8 text-center text-gray-400 text-sm" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              {t('contacts.bookingHistoryLoading')}
            </div>
          ) : error ? (
            <div className="bg-white rounded-2xl p-4 text-sm text-red-600" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              {error}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center text-gray-400 text-sm" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              {t('contacts.noBookingHistory')}
            </div>
          ) : (
            items.map(booking => {
              const depositRequired = numberOrZero(booking.deposit_required)
              const depositStatus = booking.deposit_paid ? t('contacts.depositPaid') : t('contacts.depositUnpaid')
              const depositText = depositRequired <= 0
                ? t('contacts.depositNotRequired')
                : currencyResolved
                  ? `${formatMoney(depositRequired, currency, i18n.language)} - ${depositStatus}`
                  : <><span role="status" aria-label={t('common.loading')}>…</span>{` - ${depositStatus}`}</>

              return (
                <div key={booking.id || getBookingCode(booking)} className="bg-white rounded-2xl p-4 mb-3" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <span className="font-mono text-sm font-bold text-gray-800 break-all">#{getBookingCode(booking)}</span>
                    <BookingStatusPill status={booking.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <BookingInfoRow label={t('contacts.bookingDateTime')} value={formatBookingDateTime(booking)} />
                    <BookingInfoRow label={t('contacts.partySize')} value={booking.party_size ?? booking.guests ?? booking.guest_count} />
                    <BookingInfoRow label={t('contacts.bookingSource')} value={booking.channel || booking.source} />
                    <BookingInfoRow label={t('contacts.bookingDeposit')} value={depositText} />
                  </div>
                  {booking.notes ? (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-400">{t('contacts.notes')}</div>
                      <div className="text-sm text-gray-700 leading-relaxed mt-0.5 whitespace-pre-wrap">{booking.notes}</div>
                    </div>
                  ) : null}
                </div>
              )
            })
          )}

          {total > limit ? (
            <div className="flex items-center justify-center gap-3 mt-3">
              <button
                type="button"
                className="h-9 w-9 border border-gray-200 text-gray-600 rounded-md flex items-center justify-center bg-white disabled:opacity-40"
                disabled={!hasPrevious || loading}
                onClick={() => setOffset(Math.max(0, currentOffset - limit))}
                aria-label={t('contacts.prevBookingPage')}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs font-semibold text-gray-500 min-w-[92px] text-center">
                {t('contacts.bookingPage', { current: currentPage, total: totalPages })}
              </span>
              <button
                type="button"
                className="h-9 w-9 border border-gray-200 text-gray-600 rounded-md flex items-center justify-center bg-white disabled:opacity-40"
                disabled={!hasNext || loading}
                onClick={() => setOffset(currentOffset + limit)}
                aria-label={t('contacts.nextBookingPage')}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
